import React, { useEffect, useState, useContext, useRef } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory, useLocation } from "react-router-dom";
import { Row, Col, Form } from "react-bootstrap";

import {
  getTBISymptoms,
  postNewResultingFactors,
  postRegisteredTrigger,
} from "../../api/TBIRequests";

import useListSelector from "../../lib/useListSelector";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import MedicalListSelector from "../UIComponents/MedicalListSelector";
import Sallie from "../UIComponents/Sallie";
import OnboardingStepHeader from "./OnboardingStepHeader";
import SubmitButton from "../UIComponents/SubmitButton";
import customizeMessages from "../../lib/customizeMessages";

const SelectSocialDeterminants = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [returnedData, setReturnedData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [noTrigger, setNoTrigger] = useState(false);

  const availableTriggersContainerRef = useRef(null);
  const selectedTriggersContainerRef = useRef(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);

  const history = useHistory();
  const location = useLocation();
  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  const categories = ["wellness", "travel", "screen time", "environmental", "Dietary", "situational", "Other"];
  const currentCategory = categories[currentCategoryIndex];

  const colors = {
    wellness: "#dab7ec",
    travel: "#ffdadb",
    "screen time": "#ffdcaf",
    environmental: "#fff3c4",
    Dietary: "#cef3c4",
    situational: "#c1fdf7",
    Other: "#f4f3ee",
  };

  const createItemListFromRes = (res) => {
    return categories.map((category) => {
      const items = res.data
        .filter((item) => item.subcategory === category)
        .map((item) => item.factor);
      return { category, items };
    });
  };

  // Map category+item to translation key slug
  const categoryKeyMap = {
    wellness: "wellness",
    travel: "travel",
    "screen time": "screen_time",
    environmental: "environmental",
    Dietary: "dietary",
    situational: "situational",
    Other: "other",
  };

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(90)
      : setOnboardingPercent(60);
    getTBISymptoms()
      .then((res) => {
        const itemList = createItemListFromRes(res);
        setReturnedData(itemList);
        initialize(itemList);
        setLoading(false);
      })
      .catch(() => history.push("/oops"));
  }, [t]);

  useEffect(() => {
    for (let category in selectedData) {
      if (
        selectedData[category].includes("None") ||
        selectedData[category].includes("Ninguno")
      ) {
        const selectedArrayWithoutNone = [...selectedData[category]];
        selectedArrayWithoutNone.splice(selectedArrayWithoutNone.indexOf(t("common.none") || "None"));
        selectedArrayWithoutNone.forEach((item) => {
          toggleFromSelectedList(item, selectedArrayWithoutNone, category);
        });
      }
    }
  }, [selectedData]);

  useEffect(() => {
    if (availableTriggersContainerRef.current) {
      availableTriggersContainerRef.current.scrollTop = 0;
    }
  }, [currentCategoryIndex]);

  useEffect(() => {
    if (selectedTriggersContainerRef.current) {
      selectedTriggersContainerRef.current.scrollTop =
        selectedTriggersContainerRef.current.scrollHeight;
    }
  }, [selectedData]);

  const handlePrev = () => {
    if (currentCategoryIndex > 0) setCurrentCategoryIndex((prev) => prev - 1);
  };

  const handleNextOrSubmit = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const processDataForSending = () => {
    return Object.entries(selectedData).flatMap(([category, itemList]) =>
      itemList.map((item) => ({ factor: item, category: "SDOH", subcategory: category }))
    );
  };

  const handleSubmit = () => {
    setSubmitting(true);
    const processedData = processDataForSending();
    if (processedData.length === 0) {
      setSubmitting(false);
      setNoTrigger(true);
      return;
    }
    postRegisteredTrigger({ patientId: patient.patientId, data: processedData });
    postNewResultingFactors({ patientId: patient.patientId, data: processedData })
      .then(() => {
        history.push("/selectTherapies", {
          fromPatientControl: location.state?.fromPatientControl,
        });
      })
      .catch(() => history.push("/oops"));
  };

  const getPossibleMessages = () => ({
    mainHeader: {
      Stroke: t("socialdeterminants.header"),
      tbiPatient: t("socialdeterminants.header"),
      caregiver: t("socialdeterminants.header"),
    },
    sallieText: {
      Stroke: t("socialdeterminants.sallie.patient"),
      tbiPatient: t("socialdeterminants.sallie.patient"),
      caregiver: t("socialdeterminants.sallie.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  // SMI only: shows the Sallie tip and the "Other" tab callout.
  // Checks the patient record first (set to "SMI" at registration in
  // BasicInfo.js / PatientInfo.js, same field Dashboard.js checks), with
  // user.patientType as a fallback. Other tracks see neither.
  const isSMI =
    patient?.patientType === "SMI" || user?.patientType === "SMI";
  const smiTip =
    user?.userType === "caregiver"
      ? t("socialdeterminants.smiTip.caregiver")
      : t("socialdeterminants.smiTip.patient");
  // SMI tip goes on its own line, with a blank line above it
  const sallieText = isSMI ? (
    <>
      {getMessage("sallieText")}
      <br />
      <br />
      {smiTip}
    </>
  ) : (
    getMessage("sallieText")
  );

  const messages = {
    wellness: t("socialdeterminants.messages.wellness"),
    travel: t("socialdeterminants.messages.travel"),
    "screen time": t("socialdeterminants.messages.screen_time"),
    environmental: t("socialdeterminants.messages.environmental"),
    Dietary: t("socialdeterminants.messages.dietary"),
    situational: t("socialdeterminants.messages.situational"),
    Other: t("socialdeterminants.messages.other"),
  };

  const tabs = {
    wellness: t("socialdeterminants.tabs.wellness"),
    travel: t("socialdeterminants.tabs.travel"),
    "screen time": t("socialdeterminants.tabs.screen_time"),
    environmental: t("socialdeterminants.tabs.environmental"),
    Dietary: t("socialdeterminants.tabs.dietary"),
    situational: t("socialdeterminants.tabs.situational"),
    Other: t("socialdeterminants.tabs.other"),
  };

  const tTrigger = (category, factor) => {
    const normCat = categoryKeyMap[category] || category.toLowerCase().replace(/ /g,"_");
    const safe = factor.trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    const key = `trigger.${normCat}.${safe}`;
    const translated = t(key);
    return translated !== key ? translated : factor;
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={sallieText} />

          <Row className="justify-content-between" style={{ marginTop: "1rem" }}>
            <Col sm={8}>
              <h5 style={{ textAlign: "center" }}>{t("socialdeterminants.category_header")}</h5>
            </Col>
            <Col sm={4}>
              <h5 style={{ textAlign: "center" }}>{t("socialdeterminants.list_header")}</h5>
            </Col>
          </Row>

          <Tab.Container id="left-tabs-example" activeKey={currentCategory}>
            <Row style={{ marginTop: "0.5rem" }}>
              <Col sm={3}>
                <Nav fill justify variant="tabs" className="flex-column">
                  {returnedData.map((itemList, i) => (
                    <Nav.Item key={itemList.category}>
                      <Nav.Link
                        eventKey={itemList.category}
                        style={{
                          background: `${colors[itemList.category]}`,
                          border: "1px solid #106995",
                          fontWeight: itemList.category === currentCategory ? "bold" : "normal",
                        }}
                        disabled
                      >
                        {tabs[itemList.category]}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Col>
              <Col sm={5}>
                <Tab.Content ref={availableTriggersContainerRef}>
                  {returnedData.map((itemList, i) => (
                    <Tab.Pane key={itemList.category} eventKey={itemList.category}>
                      {/* SMI only: highlighted callout in the Other tab */}
                      {isSMI && itemList.category === "Other" && (
                        <div
                          role="note"
                          style={{
                            background: "#fff3c4",
                            borderLeft: "5px solid #106995",
                            borderRadius: "5px",
                            padding: "0.75rem 1rem",
                            margin: ".25rem 0 .75rem",
                            color: "black",
                            textAlign: "left",
                          }}
                        >
                          💡 {t("socialdeterminants.smiOtherTip.text")}
                        </div>
                      )}
                      <MedicalListSelector
                        patient={patient}
                        category={itemList.category}
                        message={messages[itemList.category]}
                        list={itemList.items}
                        index={i}
                        selectedList={selectedData[itemList.category]}
                        toggleFromSelectedList={toggleFromSelectedList}
                        selectOne={switchSingleSelection}
                        addOther={addOther}
                        noOther={false}
                        translationNamespace="trigger"
                      />
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Col>
              <Col sm={4}>
                <div
                  ref={selectedTriggersContainerRef}
                  className="justify-content-left"
                  style={{
                    height: "30rem", overflow: "scroll", color: "black",
                    border: "1px solid #106995", textAlign: "left", background: "#fff",
                    padding: "2rem", borderRadius: "5px", margin: ".25rem 0",
                  }}
                >
                  {Object.entries(selectedData).flatMap(([category, itemList]) =>
                    itemList.map((item) => (
                      <div key={item} className="flex items-start gap-2 my-2">
                        <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1"
                          xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
                          className="flex-shrink-0">
                          <title>icon/action/check_ccircle_24px</title>
                          <defs>
                            <path d="M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M10,17 L5,12 L6.41,10.59 L10,14.17 L17.59,6.58 L19,8 L10,17 Z" id="path-1"></path>
                          </defs>
                          <g id="icon/action/check_ccircle_24px" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                            <mask id="mask-2" fill="white"><use xlinkHref="#path-1"></use></mask>
                            <use id="-↳Color" fill={colors[category]} xlinkHref="#path-1"></use>
                          </g>
                        </svg>{" "}{tTrigger(category, item)}
                      </div>
                    ))
                  )}
                </div>
              </Col>
            </Row>
          </Tab.Container>

          <Row>
            <Col>
              {noTrigger && (
                <Form.Control.Feedback style={{ display: "block" }} type="invalid">
                  {t("socialdeterminants.error")}
                </Form.Control.Feedback>
              )}
            </Col>
          </Row>
          <Row className="d-flex justify-content-between align-items-center" style={{ marginTop: "1rem" }}>
            <Col xs="auto">
              <SubmitButton type="button" onClick={handlePrev}
                disabled={currentCategoryIndex === 0} greyout={currentCategoryIndex === 0}>
                {t("selectlatersymptoms.previous_category")}
              </SubmitButton>
            </Col>
            <Col xs="auto">
              <SubmitButton disabled={submitting} onClick={handleNextOrSubmit}>
                {currentCategoryIndex < categories.length - 1
                  ? t("selectlatersymptoms.next_category")
                  : `${t("onboarding.saveContinue")} >`}
              </SubmitButton>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SelectSocialDeterminants;